/**
 * Kingdom Factor - KF Ambassador Page
 * Sales page for the KF Ambassador program, modeled on Mark's "KF Ambassador Brochure"
 * artifact (task #675). The Zoom walkthrough calendar is the main action: every button on
 * the page, the sign-up buttons too, scrolls to it at #walkthrough.
 *
 * Listed: linked from the header nav and in the sitemap.
 */

import { SeoHead } from "@/components/SeoHead"
import {
  ArrowRight,
  BookOpen,
  Check,
  Church,
  GraduationCap,
  Mic,
  Share2,
  Users,
} from "lucide-react"
import { useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Header } from "@/components/layout/Header"
import { Footer } from "@/components/layout/Footer"
import { CRM_EMBED_SCRIPT_URL, CRM_LINK_URL } from "@/lib/kf-crm"

const BOOKING_URL = `${CRM_LINK_URL}/widget/bookings/kfambassador`

const benefits = [
  {
    icon: Mic,
    title: "A voice in the KF Collective",
    description: "Interview Christian business leaders in your community and publish their stories on KFcollective.com.",
  },
  {
    icon: BookOpen,
    title: (
      <>
        Your own <em>Kingdom Factors</em> book
      </>
    ),
    description: "Turn your interviews into a published book using KF's proven process.",
  },
  {
    icon: GraduationCap,
    title: "The KF Marketplace Ministry course",
    description: "Complete all 9 sessions and ground yourself in the Kingdom Factor message.",
  },
  {
    icon: Church,
    title: "A study you can lead",
    description: "Facilitate the 9-session study model in churches and small groups.",
  },
  {
    icon: Users,
    title: "Ongoing community and training",
    description: "Private quarterly KF workshops and monthly best-practice sessions with fellow Ambassadors.",
  },
  {
    icon: Share2,
    title: "Social media toolkit",
    description: "Ready-to-use content and guidance for sharing marketplace ministry online.",
  },
]

const asks = [
  { lead: "Complete the KF Marketplace Ministry course", text: "within your first 90 days." },
  { lead: "Tell the stories.", text: "Interview and submit at least one Christian business leader's story to the KF Collective each quarter." },
  { lead: "Lead when the door opens.", text: "Look for opportunities to facilitate the 9-session study in your church, small group, or community." },
  { lead: "Stay connected.", text: "Participate in the quarterly workshops and monthly best-practice sessions whenever you can." },
  { lead: "Represent KF with integrity.", text: "Follow KF's interview process and guidelines so every leader featured is honored well." },
  { lead: "Share the message.", text: "Use the social media toolkit to help more believers discover the KF Collective." },
]

const walkthroughTopics = [
  {
    lead: "The vision of the KF Ambassador program:",
    text: "why we created it and where it fits in serving Christ in the marketplace",
  },
  {
    lead: "A demonstration of what you get:",
    text: (
      <>
        the interview process, a real Collective story, the Marketplace Ministry course, your{" "}
        <em>Kingdom Factors</em> book, and the social media toolkit
      </>
    ),
  },
  { lead: "Your questions, answered", text: null },
]

export default function Ambassador({ seo, settings = {} }) {
  const { scheduleCallUrl = "#" } = settings

  // Load the High Level embed script, which sizes the booking calendar iframe
  useEffect(() => {
    const script = document.createElement("script")
    script.src = CRM_EMBED_SCRIPT_URL
    script.async = true
    document.body.appendChild(script)
    return () => {
      document.body.removeChild(script)
    }
  }, [])

  return (
    <>
      <SeoHead seo={seo} />

      <div className="bg-white text-zinc-900 min-h-screen">
        <Header activePage="ambassador" scheduleCallUrl={scheduleCallUrl} />

        {/* Hero Section - Light, as on Coach.jsx */}
        <section className="bg-zinc-50 pt-32 pb-24">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-zinc-900 mb-6 leading-tight">
              KF <span className="text-kf-red">AMBASSADOR</span>
            </h1>
            <p className="text-xl md:text-2xl text-zinc-600 mb-4 font-display tracking-wide">
              Equipped and sent <span className="text-kf-red">into the marketplace</span>
            </p>
            <p className="text-lg text-zinc-500 mb-10 max-w-2xl mx-auto leading-relaxed">
              Not every leader is called to be a Kingdom Factor Coach, but every believer in
              business is called to carry the Kingdom into the marketplace. KF Ambassador gives
              you a meaningful way to serve today, with no cohort to build.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-6">
              <Button variant="gradient" size="lg" className="group w-full sm:w-auto whitespace-normal" asChild>
                <a href="#walkthrough">
                  Save my spot on the Zoom walkthrough
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button variant="kf-outline" size="lg" className="w-full sm:w-auto" asChild>
                <a href="#walkthrough">I'm in. Sign me up.</a>
              </Button>
            </div>
            <p className="text-zinc-500 text-sm">
              $99/month · No long-term contract · No cohort to build
            </p>
          </div>
        </section>

        {/* Stat Band */}
        <section className="bg-kf-gradient py-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-4">
              <span className="font-display text-5xl md:text-6xl text-white">1,000+</span>
              <p className="text-white/90 font-semibold leading-snug max-w-xs">
                members, featured leaders, and KF coaches in a transformational community
              </p>
            </div>
            <p className="font-display text-xl md:text-2xl text-white tracking-wider">
              REAL FAITH. REAL RESULTS.
            </p>
          </div>
        </section>

        {/* Something Bigger / Who It's For */}
        <section className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-12 lg:gap-16">
            <div>
              <div className="w-12 h-1 bg-kf-red mb-6" />
              <h2 className="font-display text-3xl md:text-4xl text-zinc-900 mb-4">
                BE PART OF SOMETHING <span className="text-kf-red">BIGGER</span>
              </h2>
              <p className="text-zinc-600 text-lg leading-relaxed">
                The KF Collective is a community of real Christian business leaders who see their
                work as worship and their business as a platform for the Kingdom, and we're
                building it into the largest collective of its kind in the country. Every leader
                featured on{" "}
                <a
                  href="https://kfcollective.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-kf-red underline hover:text-kf-red-dark"
                >
                  KFcollective.com
                </a>{" "}
                is a testimony that faith and business belong together.
              </p>
            </div>
            <div>
              <div className="w-12 h-1 bg-kf-red mb-6" />
              <h2 className="font-display text-3xl md:text-4xl text-zinc-900 mb-4">
                WHO IT'S <span className="text-kf-red">FOR</span>
              </h2>
              <p className="text-zinc-600 text-lg leading-relaxed">
                You're a business leader, professional, or ministry-minded believer who sees the
                marketplace as a mission field. You want to be part of what God is doing through
                Kingdom Factor, but coaching a peer group isn't your calling right now.
              </p>
            </div>
          </div>
        </section>

        {/* What You Get */}
        <section className="bg-zinc-50 py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-display text-4xl md:text-5xl text-zinc-900">
                WHAT YOU <span className="text-kf-red">GET</span>
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, i) => (
                <div
                  key={i}
                  className="bg-white p-8 rounded-lg shadow-md border-t-4 border-kf-red hover:shadow-lg hover:translate-x-2 transition-all"
                >
                  <benefit.icon className="w-10 h-10 text-kf-red mb-4" />
                  <h3 className="font-display text-xl text-zinc-900 mb-2">{benefit.title}</h3>
                  <p className="text-zinc-600 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Ask + Investment */}
        <section className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-display text-4xl md:text-5xl text-zinc-900 mb-4">
                WHAT WE <span className="text-kf-red">ASK</span>
              </h2>
              <p className="text-zinc-600 max-w-2xl mx-auto text-lg">
                Being an Ambassador is a calling, not just a subscription. We ask that you:
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10 mb-24">
              {asks.map((ask, i) => (
                <div key={i}>
                  <span className="font-display text-6xl text-kf-red/20">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-zinc-600 text-lg leading-relaxed -mt-2">
                    <span className="font-semibold text-zinc-900">{ask.lead}</span> {ask.text}
                  </p>
                </div>
              ))}
            </div>

            <div className="bg-kf-black rounded-lg p-8 lg:p-12 grid md:grid-cols-[auto_1fr] gap-8 md:gap-16 items-center">
              <div>
                <p className="text-kf-red font-semibold uppercase tracking-wider text-sm mb-2">
                  Your Investment
                </p>
                <p className="font-display text-7xl text-white leading-none">$99</p>
                <p className="text-white/60 mt-2">per month</p>
              </div>
              <div>
                <p className="text-white/80 text-lg leading-relaxed">
                  Kingdom Factor isn't trying to profit from Ambassadors. Your monthly investment
                  simply helps cover the real costs of the program: course access, the KF
                  Collective platform, editing and publishing support for your{" "}
                  <em>Kingdom Factors</em> book, workshops and training, and your social media
                  toolkit. No long-term contract. No cohort to build.
                </p>
                <Button variant="gradient" size="lg" className="group mt-8" asChild>
                  <a href="#walkthrough">
                    Become a KF Ambassador
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Zoom Walkthrough + Booking Calendar */}
        <section id="walkthrough" className="bg-zinc-50 py-24 scroll-mt-16">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mx-auto mb-12">
              <div className="text-center mb-8">
                <span className="text-kf-red text-sm uppercase tracking-[0.2em] mb-4 block">
                  30-Minute Zoom Walkthrough
                </span>
                <h2 className="font-display text-4xl md:text-5xl text-zinc-900 mb-4">
                  SEE HOW IT <span className="text-kf-red">WORKS</span>
                </h2>
                <p className="text-zinc-600 text-lg">
                  Join a short group session to see the Ambassador program up close and get your
                  questions answered.
                </p>
              </div>
              <div className="space-y-4">
                {walkthroughTopics.map((topic, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <Check className="w-5 h-5 text-kf-red mt-1 flex-shrink-0" />
                    <p className="text-zinc-600 text-lg leading-relaxed">
                      <span className="font-semibold text-zinc-900">{topic.lead}</span>
                      {topic.text && <> {topic.text}</>}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* High Level Booking Calendar Embed */}
            <div className="bg-white p-2 md:p-6 rounded-lg shadow-md">
              <iframe
                src={BOOKING_URL}
                style={{ width: "100%", minHeight: "700px", border: "none", overflow: "hidden" }}
                scrolling="no"
                id="kfambassador_booking"
                title="Book a KF Ambassador Zoom walkthrough"
              />
            </div>
          </div>
        </section>

        {/* Why It Matters - Final CTA */}
        <section className="bg-kf-gradient py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-white/70 font-semibold uppercase tracking-wider mb-4">
              Why It Matters
            </p>
            <p className="font-display text-3xl md:text-4xl text-white leading-snug mb-10">
              Every story you tell adds another leader to the Collective and shows believers that
              their work matters to God.
            </p>
            <Button variant="kf-secondary" size="lg" className="group" asChild>
              <a href="#walkthrough">
                Join the walkthrough
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
        </section>

        <Footer activePage="ambassador" />
      </div>
    </>
  )
}
